import {useMutation} from '@tanstack/react-query';
import {type} from 'arktype';
import {array, type as ioType, readonly, TypeOf} from 'io-ts';
import {VerificationMethodType} from '~/features/two-factor';
import {
    expectEither,
    HttpClient,
    Jwt,
    POST,
    RefreshToken,
    ServerValidationResult,
    usePublicHttpClient,
} from '~/library/network-facade';
import {MobilePhone, NonEmptyString, PositiveInt} from '~/library/types';
import {LegacyAddressStatus} from '../model/AddressStatus';
import {UserId} from '../model/UserId';
import {UserStatusIoTs} from '../model/UserStatus';

export type SignupStartRequest = {
    username: string;
    repeated_event?: string;
    invite_code: string;
    onesignal_id?: string;
    onesignal_consent?: boolean;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    referer?: string;
    tenant_uuid: string;
    captchaToken: string;
    verificationMethod?: VerificationMethodType;
    device_id?: string;
    app_version?: string;
};

export const SignupStartErrorCode = {
    requestLimit: 'request_limit',
    alreadyRegistered: 'already_registered',
    cooldownPeriod: 'cooldown_period',
    somethingWrong: 'something_wrong',
} as const;
export type SignupStartErrorCode = keyof typeof SignupStartErrorCode;

export const SignupStartResponse = type({
    code: 'number',
    message: 'string',
    tenant_uuid: 'string',
    retry_at: 'number',
    retry_in: 'number',
});
export type SignupStartResponse = typeof SignupStartResponse.infer;

export function signupStart(http: HttpClient, data: SignupStartRequest) {
    return http('v1/sign_up', POST(data)).then(
        ...expectEither({
            200: SignupStartResponse,
            400: type({
                error_code: type.enumerated(SignupStartErrorCode.somethingWrong),
            }),
            422: type({
                error_code: type.enumerated(SignupStartErrorCode.alreadyRegistered),
            }),
            429: type({
                retry_at: 'number',
                retry_in: 'number',
                error_code: type.enumerated(SignupStartErrorCode.cooldownPeriod, SignupStartErrorCode.requestLimit),
            }),
        })
    );
}

export function useSignupStartMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/sign_up'],
        async mutationFn(data: SignupStartRequest) {
            const res = await signupStart(http, data);

            switch (res.status) {
                case 200: {
                    return {value: res.json};
                }

                case 'Abort': {
                    throw res.reason;
                }

                default: {
                    return {problem: res.json};
                }
            }
        },
    });
}

export type SignupVerifyCodeRequest = {
    phone: string;
    code: string;
};

export const SignupVerifyCodeErrorCode = {
    wrongCode: 'wrong_code',
    codeExpired: 'code_expired',
    limitReached: 'code_verification_limit_reached',
    somethingWrong: 'something_wrong',
} as const;
export type SignupVerifyCodeErrorCode = keyof typeof SignupStartErrorCode;

export function signupVerifyCode(http: HttpClient, data: SignupVerifyCodeRequest) {
    return http('v1/verify_code', POST(data)).then(
        ...expectEither({
            200: type({
                value: 'string',
            }),
            400: type({
                error_code: type.enumerated(SignupVerifyCodeErrorCode.somethingWrong),
            }),
            422: type({
                error_code: type.enumerated(SignupVerifyCodeErrorCode.wrongCode, SignupVerifyCodeErrorCode.codeExpired),
            }),
            429: type({
                error_code: type.enumerated(SignupVerifyCodeErrorCode.limitReached),
                retry_in: 'number',
            }),
        })
    );
}

export function useSignupVerifyCodeMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/verify_code'],
        mutationFn: async (data: SignupVerifyCodeRequest) => {
            const res = await signupVerifyCode(http, data);

            switch (res.status) {
                case 'Abort': {
                    throw res.reason;
                }

                case 200: {
                    return {value: res.json.value};
                }

                default: {
                    return {problem: res.json};
                }
            }
        },
    });
}

type SetupPasswordRequest = {
    password: string;
    verificationSession: string;
};

export const SignupSetupPasswordResponse = readonly(
    ioType({
        access_token: Jwt,
        expires_in: PositiveInt,
        refresh_token: RefreshToken,
        refresh_token_expiration: PositiveInt,
        user: readonly(
            ioType({
                id: UserId,
                username: MobilePhone,
                status: UserStatusIoTs,
                roles: readonly(array(NonEmptyString)),
                address_status: LegacyAddressStatus,
            })
        ),
    })
);
export type SignupSetupPasswordResponse = TypeOf<typeof SignupSetupPasswordResponse>;

export const SignupSetupPasswordErrorCode = {
    somethingWrong: 'something_wrong',
    sessionNotFound: 'session_not_found',
    sessionNotVerified: 'session_not_verified',
    validationFailed: 'validation_failed',
} as const;
export type SignupSetupPasswordErrorCode = keyof typeof SignupSetupPasswordErrorCode;

export const signupSetupPassword = (http: HttpClient, data: SetupPasswordRequest) =>
    http('v1/setup_password', POST(data)).then(
        ...expectEither({
            200: SignupSetupPasswordResponse,
            400: type({
                '+': 'reject',
                'code': 'number',
                'message': 'string',
                'error_code': type.enumerated(SignupSetupPasswordErrorCode.somethingWrong),
            }).or(
                ServerValidationResult.pipe(
                    type({
                        password: 'string?',
                    })
                )
            ),
            422: type({
                error_code: type.enumerated(
                    SignupSetupPasswordErrorCode.sessionNotFound,
                    SignupSetupPasswordErrorCode.sessionNotVerified
                ),
            }),
        })
    );

export function useSignupSetupPasswordMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/setup_password'],
        async mutationFn(data: SetupPasswordRequest) {
            const res = await signupSetupPassword(http, data);

            switch (res.status) {
                case 200: {
                    return {value: res.json};
                }
                case 400: {
                    if ('error_code' in res.json) {
                        return {problem: res.json};
                    }
                    return {
                        problem: {
                            error_code: SignupSetupPasswordErrorCode.validationFailed,
                            issues: res.json,
                        },
                    };
                }
                case 'Abort': {
                    throw res.reason;
                }
                default: {
                    return {problem: res.json};
                }
            }
        },
    });
}
